'use client'

import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { formatVersion } from '@/components/ui/SidebarLogo'
import { cinzel } from '@/lib/brand-font'

// ─────────────────────────────────────────────────────────────────────────────
// The OMNEX brain experience (brain-scene.js = OMNEX I's BRAIN V13 on bundled
// three@0.128.0). Props:
//   hero      — 100vh section for the landing page (wheel scrolls, no zoom)
//   skipIntro — /login: no 20s journey; brain already out, sign-in modal open
//   onCta     — CTA navigates instead of opening the modal (landing)
//
// The wordmark uses ONE shared lettering (.ab-word) for the splash and the
// end title, so OMNEX looks identical both times it appears.
// CSS is a plain SSR'd <style> tag (styled-jsx injects at hydration, which
// caused the splash flash).
// ─────────────────────────────────────────────────────────────────────────────

const CSS = `
.ab-root{position:fixed;inset:0;z-index:0;background:#000;overflow:hidden;color:#e5be62}
.ab-root.ab-hero{position:relative;inset:auto;height:100vh;min-height:640px;width:100%}
.ab-root canvas{display:block;position:absolute;top:0;left:0;z-index:0;cursor:grab}
.ab-root canvas:active{cursor:grabbing}

/* ── The power word — SAME lettering everywhere OMNEX appears.
   font-family pinned explicitly: the global h1 rule was overriding the
   inherited Cinzel on the closing title, which is why the two OMNEX
   wordmarks looked different. Metallic gold: gradient-filled letters with
   a slow sheen sweep + glow — bling to match the brain. ── */
.ab-root .ab-word{
  font-family:${cinzel.style.fontFamily};
  font-weight:700;text-transform:uppercase;
  background:
    linear-gradient(115deg,transparent 22%,rgba(255,248,220,.5) 30%,transparent 38%) no-repeat,
    linear-gradient(180deg,#fff3c4 0%,#f8d97e 38%,#e3b755 62%,#c99a3e 100%);
  background-size:250% 100%,100% 100%;
  background-position:-140% 0,0 0;
  -webkit-background-clip:text;background-clip:text;
  color:transparent;-webkit-text-fill-color:transparent;
  filter:drop-shadow(0 0 16px rgba(248,217,126,.45)) drop-shadow(0 0 55px rgba(240,200,110,.25));
  animation:ab-sheen 6s ease-in-out 3s infinite;
}
@keyframes ab-sheen{
  0%,55%{background-position:-140% 0,0 0}
  85%,100%{background-position:240% 0,0 0}
}
.ab-root .ab-word-tag{
  text-transform:uppercase;color:rgba(240,205,125,.72);
  letter-spacing:clamp(4px,1vw,10px);font-size:clamp(9px,1.2vw,13px);
}

/* ── SPLASH ── */
.ab-root #splash{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;flex-direction:column;background:transparent;z-index:200;opacity:1;cursor:pointer;transition:opacity 3.5s ease}
.ab-root #splash.out{opacity:0;pointer-events:none}
.ab-root #splash .brand{
  font-size:clamp(42px,7.2vw,96px);
  letter-spacing:clamp(12px,3vw,38px);
  opacity:0;
  animation:
    ab-brandIn 3.4s cubic-bezier(.16,1,.3,1) .8s forwards,
    ab-sheen 6s ease-in-out 4.6s infinite;
}
.ab-root #splash .rule{width:0;height:1px;background:linear-gradient(90deg,transparent,rgba(240,205,120,.6),transparent);margin:22px 0;animation:ab-ruleIn 2.8s cubic-bezier(.16,1,.3,1) 2.2s forwards}
.ab-root #splash .sub{opacity:0;animation:ab-subIn 2.6s cubic-bezier(.16,1,.3,1) 2.7s forwards}
@keyframes ab-brandIn{
  from{opacity:0;transform:scale(.94);letter-spacing:clamp(20px,5vw,60px);filter:blur(8px) drop-shadow(0 0 16px rgba(248,217,126,.45)) drop-shadow(0 0 55px rgba(240,200,110,.25))}
  60%{opacity:1}
  to{opacity:1;transform:scale(1);letter-spacing:clamp(12px,3vw,38px);filter:blur(0) drop-shadow(0 0 16px rgba(248,217,126,.45)) drop-shadow(0 0 55px rgba(240,200,110,.25))}
}
@keyframes ab-subIn{from{opacity:0;transform:translateY(5px)}to{opacity:1;transform:translateY(0)}}
@keyframes ab-ruleIn{from{width:0}to{width:clamp(120px,18vw,250px)}}

/* ── TITLE — the second OMNEX: identical letters, sized for the frame ── */
.ab-root #omnex-title{position:absolute;bottom:6%;left:50%;transform:translateX(-50%) translateY(20px);z-index:10;text-align:center;pointer-events:none;opacity:0;transition:opacity 3.5s ease,transform 3.5s cubic-bezier(.16,1,.3,1)}
.ab-root #omnex-title.show{opacity:1;transform:translateX(-50%) translateY(0)}
.ab-root #omnex-title h1{
  margin:0;
  font-size:clamp(34px,5vw,68px);
  letter-spacing:clamp(11px,2.6vw,34px);
}
.ab-root #omnex-title .tag{margin-top:11px}

/* ── Sign in — top right ── */
.ab-root #login-area{position:absolute;top:26px;right:34px;z-index:120;opacity:0;transition:opacity 1.5s ease}
.ab-root #login-area.show{opacity:1}
.ab-root #login-btn,.ab-root .ab-nav-cta{
  background:rgba(28,20,9,.55);
  border:1px solid rgba(240,205,120,.7);
  color:#f6d88f;
  font-family:inherit;font-weight:700;font-size:13px;letter-spacing:4px;text-transform:uppercase;
  padding:12px 32px;cursor:pointer;transition:all .35s;backdrop-filter:blur(6px);
  box-shadow:0 0 30px rgba(240,205,120,.22),inset 0 0 16px rgba(240,205,120,.07);
}
.ab-root #login-btn:hover,.ab-root .ab-nav-cta:hover{background:rgba(240,205,120,.24);border-color:#f6d478;color:#ffedbe;box-shadow:0 0 44px rgba(246,212,120,.42)}

/* ── Modal — the brain stays visible behind the card ── */
.ab-root #login-modal{display:none;position:absolute;inset:0;z-index:100;align-items:center;justify-content:center;background:rgba(0,0,0,.5);backdrop-filter:blur(4px)}
.ab-root #login-modal.active{display:flex}
.ab-root .lbox{
  position:relative;width:440px;max-width:94vw;padding:54px 52px 50px;
  border:1px solid rgba(240,205,120,.55);
  background:linear-gradient(180deg,rgba(26,19,10,.93),rgba(9,7,4,.96));
  box-shadow:0 40px 120px rgba(0,0,0,.85),0 0 90px rgba(240,205,120,.14),inset 0 0 80px rgba(240,205,120,.05);
  animation:ab-boxI .45s cubic-bezier(.16,1,.3,1) forwards;
}
.ab-root .lbox::before{content:'';position:absolute;inset:7px;border:1px solid rgba(240,205,120,.2);pointer-events:none}
.ab-root .lbox .lmark{display:block;text-align:center;font-size:17px;color:#f6d478;margin-bottom:14px;text-shadow:0 0 18px rgba(246,212,120,.6)}
.ab-root .lbox h2{margin:0 0 6px;font-weight:700;font-size:28px;letter-spacing:12px;text-transform:uppercase;text-align:center;color:#f6d478;text-shadow:0 0 26px rgba(246,212,120,.4)}
.ab-root .lbox .lsub{text-align:center;font-size:10px;letter-spacing:4px;text-transform:uppercase;color:rgba(240,205,125,.55);margin-bottom:8px}
.ab-root .lbox .lrule{display:block;width:100px;height:2px;margin:18px auto 30px;background:linear-gradient(90deg,transparent,rgba(240,205,120,.75),transparent)}
.ab-root .lbox label{font-size:11px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:rgba(244,210,130,.9);display:block;margin-bottom:8px}
.ab-root .lbox input[type='password'],
.ab-root .lbox input[type='email'],
.ab-root .lbox input[type='text']{
  width:100%;border-radius:2px;
  background:rgba(240,205,120,.07);
  border:1px solid rgba(240,205,120,.4);
  color:#f8e2ae;font-family:inherit;font-size:16px;letter-spacing:1.5px;
  padding:15px 16px;margin-bottom:24px;outline:none;transition:border-color .25s,box-shadow .25s,background .25s;
  caret-color:#f6d478;box-sizing:border-box;
}
.ab-root .lbox input::placeholder{color:rgba(240,205,120,.38);letter-spacing:2px;text-transform:uppercase;font-size:12px}
.ab-root .lbox input:focus{border-color:#f0cd75;background:rgba(240,205,120,.12);box-shadow:0 0 0 1px rgba(240,205,120,.4),0 0 26px rgba(240,205,120,.16)}
.ab-root .lbox button[type='submit']{
  width:100%;border-radius:2px;
  background:linear-gradient(180deg,#eec96a,#bd8f3c);
  border:1px solid #f0cd75;color:#171006;
  font-family:inherit;font-size:15px;font-weight:700;letter-spacing:6px;text-transform:uppercase;
  padding:17px;cursor:pointer;margin-top:10px;transition:all .3s;
  box-shadow:0 8px 30px rgba(240,205,120,.3);
}
.ab-root .lbox button[type='submit']:hover:not(:disabled){background:linear-gradient(180deg,#f8d87e,#d2a24a);box-shadow:0 10px 42px rgba(246,212,120,.45);transform:translateY(-1px)}
.ab-root .lbox button[type='submit']:disabled{opacity:.5;cursor:default;transform:none}
.ab-root .lclose{position:absolute;top:28px;right:36px;color:rgba(240,205,120,.6);font-size:32px;cursor:pointer;background:none;border:none;font-family:Georgia,serif;transition:color .3s;z-index:101}
.ab-root .lclose:hover{color:#f6d478}
.ab-root .lbox .alert-msg{background:rgba(200,100,60,.14);border:1px solid rgba(200,100,60,.4);color:#f0b088;padding:11px 14px;font-size:13px;letter-spacing:1px;margin-bottom:20px;text-align:center;border-radius:2px}

.ab-root #ver{position:absolute;top:30px;left:36px;z-index:20;font-size:9px;letter-spacing:3px;text-transform:uppercase;color:rgba(240,205,120,.4);opacity:0;transition:opacity 1.5s ease}
.ab-root #ver.show{opacity:1}

@keyframes ab-boxI{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
`

export default function AuthBrain({
  children,
  hero = false,
  skipIntro = false,
  ctaLabel = 'Sign In',
  onCta,
}: {
  children?: ReactNode
  hero?: boolean
  skipIntro?: boolean
  ctaLabel?: string
  onCta?: () => void
}) {
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    let cancelled = false
    let cleanup: (() => void) | undefined
    let heroTitleTimer: ReturnType<typeof setTimeout> | undefined

    // If WebGL/scene init ever fails, don't strand the user on a black page.
    const reveal = () => {
      const splash = document.getElementById('splash')
      if (splash) splash.style.display = 'none'
      document.getElementById('omnex-title')?.classList.add('show')
      document.getElementById('login-area')?.classList.add('show')
      document.getElementById('ver')?.classList.add('show')
    }

    import('./brain-scene')
      .then((m) => {
        if (cancelled) return
        try {
          // Hero (landing): wheel scrolls the page, never zooms the brain.
          // skipIntro (/login): straight to the signed-out state, modal open.
          cleanup = m.startBrain(root, { noWheelZoom: hero, skipIntro })
        } catch (e) {
          console.error('[AuthBrain] scene failed, falling back:', e)
          reveal()
        }
      })
      .catch((e) => {
        console.error('[AuthBrain] scene chunk failed to load:', e)
        reveal()
      })

    // Landing: show the slogan soon after the splash instead of at 18s.
    if (hero) {
      heroTitleTimer = setTimeout(() => {
        document.getElementById('omnex-title')?.classList.add('show')
      }, 7500)
    }

    return () => {
      cancelled = true
      if (heroTitleTimer) clearTimeout(heroTitleTimer)
      cleanup?.()
    }
  }, [hero, skipIntro])

  return (
    <div ref={rootRef} className={`ab-root ${hero ? 'ab-hero' : ''} ${cinzel.className}`}>
      {/* SSR'd styles — must arrive with the very first paint (see header note) */}
      <style dangerouslySetInnerHTML={{ __html: CSS }} />

      {/* the V13 scene appends its <canvas> here */}

      {/* v728 — skipIntro (/login) renders the SIGNED-OUT END STATE directly in
          the HTML: splash gone, modal open, chrome shown. brain-scene.js used
          to be the only thing that produced it (`if (hasError)` + openLogin),
          so with no JS the form was `display:none` — invisible and unusable,
          which made v727's native fallback pointless to an actual human even
          though the endpoint worked. The scene re-applies these same classes
          idempotently, so the animation is unchanged; it is now decoration on
          top of a page that already works. The splash element must still EXIST
          (brain-scene.js reads `.style` on it) — it is only hidden.
          The landing page (skipIntro=false) keeps the full 20s journey. */}
      <div id="splash" style={skipIntro ? { display: 'none' } : undefined}>
        <div className="brand ab-word">Omnex</div>
        <div className="rule" />
        <div className="sub ab-word-tag">Personal Knowledge Architecture</div>
      </div>

      <div id="omnex-title" className={skipIntro ? 'show' : undefined}>
        <h1 className="ab-word">Omnex</h1>
        <div className="tag ab-word-tag">Personal Knowledge Architecture</div>
      </div>

      <div id="ver" className={skipIntro ? 'show' : undefined}>Omnex {formatVersion()}</div>

      <div id="login-area" className={skipIntro ? 'show' : undefined}>
        {onCta ? (
          <>
            {/* the scene requires #login-btn to exist; the visible CTA navigates */}
            <button id="login-btn" style={{ display: 'none' }} aria-hidden="true" />
            <button className="ab-nav-cta" onClick={onCta}>{ctaLabel}</button>
          </>
        ) : (
          /* skipIntro (/login): the card IS the page — no redundant corner button */
          <button id="login-btn" style={skipIntro ? { display: 'none' } : undefined}>{ctaLabel}</button>
        )}
      </div>

      {/* `active` from the server on /login — without it the modal is
          display:none and the sign-in form does not exist for a no-JS user. */}
      <div id="login-modal" className={skipIntro ? 'active' : undefined}>
        {/* the scene requires #login-close-btn to exist; hidden when the modal is the page */}
        <button className="lclose" id="login-close-btn" style={skipIntro ? { display: 'none' } : undefined}>×</button>
        <div className="lbox">
          <span className="lmark">✦</span>
          <h2 className="ab-word">Omnex</h2>
          <div className="lsub">Personal Knowledge Architecture</div>
          <div className="lrule" />
          {children}
        </div>
      </div>
    </div>
  )
}
